import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { FeedbackDialogComponent } from '@cpd-app/core-module/components/feedback-dialog/feedback-dialog.component';
import {
  ChatMessage,
  ConversationGoals,
  RateLimit
} from '@cpd-app/core-module/models';
import { ViaNudgesService } from '@cpd-app/core-module/services';
import { GoalCelebrationService } from '@cpd-app/core-module/services/goal-celebration/goal-celebration.service';
import { AiChatFacade } from '@cpd-app/state/ai-chat';
import { CognitoAuthFacade } from '@cpd-app/state/cognito-auth';
import { GoalKey } from '@cpd-app/features/academic-goal/components/goal-set-dialog/goal-set-dialog.component';
import { GoalFacade } from '@cpd-app/state/goal';
import { GoalsResponse } from '@cpd-app/state/goal/goal.response';
import { UserProfileFacade } from '@cpd-app/state/user-profile';
import { delay, noop, Subscription, take, timer } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'cpd-conversation',
  templateUrl: './conversation.component.html',
  styleUrls: [
    './conversation.component.scss',
    './conversation-content.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConversationComponent implements AfterViewInit, OnDestroy {
  @ViewChild('messageContent')
  public messageContent!: ElementRef<HTMLDivElement>;

  @Input() public messages: ChatMessage[] = [];
  @Input() public questionForm: FormGroup;
  @Input() public isLoading = false;
  @Input() public isStreaming = false;
  @Input() public streamingResponse = '';
  @Input() public rateLimit: RateLimit | null = null;
  @Input() public isFullScreen = false;
  /**
   * V-1. The turn's error, so the panel can SAY something went wrong.
   *
   * It was written into NgRx state by the streamFailure reducer, exposed as
   * error$ on the container, and consumed exactly once -- as a console.error.
   * Searching all seventeen Via templates for an error render returned nothing.
   * A learner saw a spinner that never stopped and a locked input, and only a
   * page reload recovered.
   */
  @Input() public error: string | null = null;

  @Output() public sendMessage = new EventEmitter<null | string>();

  #showReassuringMessage = signal(false);
  #copyMessage = signal(false);
  protected readonly occupationTitle = computed(
    () => this.viaNudgesService.currentOccupation()?.title
  );

  public isSkillsSurveyComplete$ =
    this.userProfileFacade.isSkillsSurveyComplete$;
  public goals$ = this.goalFacade.goals$;
  public currentConversationId$ = this.aiChatFacade.currentConversationId$;

  public readonly today = new Date().toISOString().split('T')[0];

  public showReassuringMessage = this.#showReassuringMessage.asReadonly();
  public copyMessage = this.#copyMessage.asReadonly();

  private readonly SCROLL_DELAY = 300;
  private readonly REASSURING_MESSAGE_DELAY = 10000;

  /**
   * CPD-673. Injected with inject(), not as a constructor parameter, so the
   * constructor's positional list stays what hand-built specs pass.
   */
  private readonly goalCelebration = inject(GoalCelebrationService);

  private subscriptions = new Subscription();
  private set subscribedTo(sub: Subscription) {
    this.subscriptions.add(sub);
  }

  constructor(
    private readonly aiChatFacade: AiChatFacade,
    private readonly goalFacade: GoalFacade,
    private readonly goalsResponse: GoalsResponse,
    private readonly destroyRef: DestroyRef,
    private readonly dialog: MatDialog,
    private readonly userProfileFacade: UserProfileFacade,
    private readonly cognitoAuthFacade: CognitoAuthFacade,
    private readonly viaNudgesService: ViaNudgesService
  ) {}

  public ngAfterViewInit(): void {
    this.handleScrollToBottom();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * V-1. Re-send the question the failed turn was for.
   *
   * The learner's message IS already saved server-side before generation, so it
   * is still in `messages` -- the failure only lost the reply. Re-emitting it
   * costs a credit, which is correct: a retry is a new generation. The button
   * is a plain retry rather than an automatic one for the same reason.
   */
  protected retryLastMessage(): void {
    const lastUser = [...(this.messages ?? [])]
      .reverse()
      .find((m) => m.role === 'user');
    if (!lastUser?.content) {
      return;
    }
    this.sendMessage.emit(lastUser.content);
  }

  public onSubmit(): void {
    if (
      this.questionForm.invalid ||
      this.isStreaming ||
      this.isLoading ||
      !this.rateLimit?.allowed
    ) {
      return;
    }
    this.cognitoAuthFacade.isAuthenticated$
      .pipe(
        take(1),
        filter((isAuthenticated) => isAuthenticated)
      )
      .subscribe({
        next: () => this.handleShowReassuringMessage(),
        error: noop
      });
    const occupationOnet = this.viaNudgesService.currentOccupation()?.onetCode;

    this.sendMessage.emit(
      !this.messages.length && occupationOnet ? occupationOnet : null
    );
  }

  public setAsCareerGoal(goals: ConversationGoals): void {
    this.goalFacade.setGoalsFromAiChat({ onetCode: goals.onetCode });

    this.listenToShowGoalDialog('career');
  }

  public setAsSchoolGoal(goals: ConversationGoals): void {
    this.goalFacade.setGoalsFromAiChat({
      onetCode: goals.onetCode,
      schoolId: goals.schoolId
    });

    this.listenToShowGoalDialog('school');
  }

  public setAsAcademicProgramGoal(goals: ConversationGoals): void {
    this.goalFacade.setGoalsFromAiChat(goals);

    this.listenToShowGoalDialog('program');
  }

  public setAsLearningSuggestions(messageId: number): void {
    this.currentConversationId$.pipe(take(1)).subscribe({
      next: (conversationId) => {
        this.goalFacade.setLearningSuggestions(conversationId, messageId);

        this.listenToShowGoalDialog();
      }
    });
  }

  public onLikeMessage(messageId: number, liked: boolean): void {
    this.aiChatFacade.likeMessage(messageId, !liked);
  }

  public onDislikeMessage(messageId: number, disliked: boolean): void {
    this.aiChatFacade.dislikeMessage(messageId, !disliked);

    if (!disliked) {
      this.dialog.open(FeedbackDialogComponent, { ariaLabel: 'Send feedback' });
    }
  }

  public onCopyMessage(messageContent: string): void {
    this.#copyMessage.set(true);
    navigator.clipboard.writeText(messageContent);

    timer(1500).subscribe(() => {
      this.#copyMessage.set(false);
    });
  }

  private scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
    if (this.messageContent?.nativeElement) {
      this.messageContent.nativeElement.scrollTo({
        top: this.messageContent.nativeElement.scrollHeight,
        behavior
      });
    }
  }

  /**
   * CPD-673. The CURRENT confirmation, not the legacy one.
   *
   * This opened `GoalDialogComponent`, the blue-banded dialog the team
   * migrated away from, so a goal set from Via confirmed itself the old way
   * while the same goal set on the occupation page raised
   * `cpd-goal-set-dialog`. Raised through GoalCelebrationService, as every
   * other place that sets a goal does (CPD-664, GR-15), and still only once the
   * save has succeeded.
   *
   * No key means no single goal was set (learning suggestions), so the dialog
   * opens for review rather than inventing a goal to celebrate.
   */
  private listenToShowGoalDialog(key?: GoalKey): void {
    this.goalsResponse.goalsUpdatedSuccess$.pipe(take(1)).subscribe(() => {
      if (key) {
        this.goalCelebration.celebrate(key);
      } else {
        this.goalCelebration.review();
      }
    });
  }

  private handleShowReassuringMessage(): void {
    this.#showReassuringMessage.set(false);

    this.subscribedTo = timer(this.REASSURING_MESSAGE_DELAY)
      .pipe(take(1))
      .subscribe(() => {
        this.#showReassuringMessage.set(true);
      });

    this.listenToStreamChunk();
  }

  private listenToStreamChunk(): void {
    this.subscribedTo = this.aiChatFacade.streamChunk$.subscribe(() => {
      this.#showReassuringMessage.set(false);
      this.subscriptions.unsubscribe();
    });
    this.subscribedTo = this.aiChatFacade.streamComplete$.subscribe(() => {
      this.#showReassuringMessage.set(false);
      this.subscriptions.unsubscribe();
    });
    this.subscribedTo = this.aiChatFacade.streamFailure$.subscribe(() => {
      this.#showReassuringMessage.set(false);
      this.subscriptions.unsubscribe();
    });
  }

  private handleScrollToBottom(): void {
    this.scrollToBottom();

    this.aiChatFacade.streamStart$
      .pipe(takeUntilDestroyed(this.destroyRef), delay(this.SCROLL_DELAY))
      .subscribe(() => {
        this.scrollToBottom();
      });

    this.aiChatFacade.streamComplete$
      .pipe(takeUntilDestroyed(this.destroyRef), delay(this.SCROLL_DELAY))
      .subscribe(() => {
        this.scrollToBottom();
      });

    this.aiChatFacade.getConversationHistorySuccess$
      .pipe(takeUntilDestroyed(this.destroyRef), delay(this.SCROLL_DELAY))
      .subscribe(() => {
        this.scrollToBottom();
      });
  }
}
